import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { base44 } from "@/api/base44Client";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { LanguageProvider, useLanguage } from "./components/LanguageContext";
import { RegionProvider, useRegion, WAREHOUSES } from "./components/RegionContext";
import { ShoppingCart, Info, Home, Phone, Package, Users, MessageCircle, Facebook, Youtube, Instagram, Menu, X, Box, BookOpen, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import GeoGate from "@/components/GeoGate";

function LayoutContent({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { warehouse, loading: regionLoading } = useRegion();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const { saveTabState, getTabState, saveScrollPosition, getScrollPosition } = useTabNavigation();
  const lastTabRef = useRef(null);
  
  // Apply RTL direction on <html> based on detected language.
  // useLayoutEffect: runs BEFORE paint so the page never flips direction
  // after rendering (was causing a huge Cumulative Layout Shift).
  useLayoutEffect(() => {
    if (language === "ar") {
      document.documentElement.dir = "rtl";
      document.documentElement.lang = "ar";
    } else {
      document.documentElement.dir = "ltr";
      document.documentElement.lang = "en";
    }
  }, [language]);
  
  // Determine if we're on a sub-page (not home)
  const isSubPage = location.pathname !== '/' && location.pathname !== createPageUrl("Home");
  
  // Tab navigation paths
  const tabPaths = {
    home: createPageUrl("Home"),
    product: createPageUrl("Product"),
    howToUse: createPageUrl("HowToUse"),
    contact: createPageUrl("Contact")
  };
  
  // Determine current tab
  const getCurrentTab = () => {
    const path = location.pathname;
    if (path === tabPaths.home || path === '/') return 'home';
    if (path.includes('/Product')) return 'product';
    if (path.includes('/HowToUse')) return 'howToUse';
    if (path.includes('/Contact')) return 'contact';
    return null;
  };
  
  // Save scroll position before navigation
  useEffect(() => {
    const currentTab = getCurrentTab();
    if (currentTab && lastTabRef.current !== currentTab) {
      if (lastTabRef.current) {
        saveScrollPosition(lastTabRef.current, window.scrollY);
        saveTabState(lastTabRef.current, location);
      }
      lastTabRef.current = currentTab;
    }
  }, [location.pathname]);
  
  // Restore scroll position on mount/navigation
  useEffect(() => {
    const currentTab = getCurrentTab();
    if (currentTab) {
      const savedPosition = getScrollPosition(currentTab);
      setTimeout(() => {
        window.scrollTo({ top: savedPosition, behavior: 'instant' });
      }, 50);
    }
  }, [location.pathname]);
  
  // Pull-to-refresh implementation
  useEffect(() => {
    let startY = 0;
    let currentY = 0;
    
    const handleTouchStart = (e) => {
      if (window.scrollY === 0) {
        startY = e.touches[0].pageY;
      }
    };
    
    const handleTouchMove = (e) => {
      if (window.scrollY === 0 && startY > 0) {
        currentY = e.touches[0].pageY;
        const distance = currentY - startY;
        
        if (distance > 0 && distance < 100) {
          e.preventDefault();
          setPullDistance(distance);
          setIsPulling(true);
        }
      }
    };
    
    const handleTouchEnd = () => {
      if (pullDistance > 60) {
        window.location.reload();
      }
      setIsPulling(false);
      setPullDistance(0);
      startY = 0;
    };
    
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [pullDistance]);

  const navLinks = [
    { name: t("nav_home"), path: createPageUrl("Home"), icon: Home },
    { name: t("nav_product"), path: createPageUrl("Product"), icon: Package },
    { name: t("nav_how_to_use"), path: createPageUrl("HowToUse"), icon: Info },
    { name: t("nav_about"), path: createPageUrl("About"), icon: Info },
    { name: t("nav_blog"), path: createPageUrl("Blog"), icon: Info },
    { name: t("nav_b2b"), path: createPageUrl("Affiliate"), icon: Users },
    { name: t("nav_contact"), path: createPageUrl("Contact"), icon: Phone },
    { name: t("nav_track_order"), path: createPageUrl("TrackOrder"), icon: Truck }
  ];

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  // REMOVED: Global model-viewer script loading to prevent conflicts

  // SEO Meta Tags per page
  useEffect(() => {
    const pageTitles = {
      '/': 'LCTX Tire Changer Machine | Air-Powered Portable Tyre Changer | UAE & Saudi Arabia',
      '/Product': 'LCTX Air-Powered Tire Changer | Professional Portable Tyre Changing Machine',
      '/HowToUse': 'How To Use LCTX Tire Changer | Installation & Operation Guide',
      '/Checkout': 'Secure Checkout | Buy LCTX Tire Changer Machine',
      '/About': 'About LCTX | Leading Tire Changer Manufacturer',
      '/Blog': 'LCTX Blog | Tire Changing Tips & Industry News',
      '/Contact': 'Contact LCTX | Tire Changer Support | UAE & Saudi Arabia',
      '/Affiliate': 'LCTX Affiliate Program | Earn with Tire Changer Sales',
      '/Upload3DModel': 'Upload 3D Model | Share Your Tire Changer Designs' // Added meta title for Upload 3D Model
    };

    const pageDescriptions = {
      '/': 'LCTX Air-Powered Tire Changer - The Middle East\'s #1 portable tyre changing machine. No electricity needed, 12-21" wheels, 2 ton torque. Free shipping. Buy now $699.',
      '/Product': 'Professional LCTX pneumatic tire changer machine. Portable, air-powered design for mobile mechanics. Handles 12-21" wheels. Order today with free shipping across UAE & Saudi Arabia.',
      '/HowToUse': 'Step-by-step guide on how to use the LCTX portable tire changer. Learn installation, operation, and best practices for efficient tire changing. Videos included.',
      '/Checkout': 'Buy LCTX Tire Changer securely with Stripe or PayPal. Free shipping across UAE & Saudi Arabia. 30-day return policy. Professional tyre changing made easy.',
      '/About': 'LCTX manufactures professional-grade portable tire changers. Learn about our pneumatic tyre changing solutions for mechanics and DIY enthusiasts.',
      '/Blog': 'Expert tire changing tips, maintenance guides, and industry news from LCTX. Learn how to change tyres efficiently with our portable machines.',
      '/Contact': 'Contact LCTX for tire changer support in UAE & Saudi Arabia, sales inquiries, and technical assistance. Email: hkjenholtd@gmail.com, Phone: +852-63613177',
      '/Affiliate': 'Join the LCTX Affiliate Program and earn commission selling portable tire changers. High conversion rates, competitive commissions, marketing support.',
      '/Upload3DModel': 'Upload and showcase your 3D models of tire changers or related automotive tools. Share your designs with the LCTX community.' // Added meta description for Upload 3D Model
    };

    document.title = pageTitles[location.pathname] || 'LCTX Tire Changer Worldwide';

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = pageDescriptions[location.pathname] || 'LCTX Air-Powered Tire Changer - Professional portable tyre changing solutions for UAE & Saudi Arabia.';

    // Preconnect only to critical domains - reduced list for faster initial load
    const preconnectDomains = [
      'https://res.cloudinary.com', // Critical for images
      'https://qtrypzzcjebvfcihiynt.supabase.co' // Critical for app data
    ];

    preconnectDomains.forEach(domain => {
      let link = document.querySelector(`link[rel="preconnect"][href="${domain}"]`);
      if (!link) {
        link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = domain;
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      }
    });
    
    // DNS prefetch for non-critical domains
    const dnsPrefetchDomains = [
      'https://www.googletagmanager.com',
      'https://www.google-analytics.com',
      'https://www.paypal.com',
      'https://js.stripe.com'
    ];
    
    dnsPrefetchDomains.forEach(domain => {
      let link = document.querySelector(`link[rel="dns-prefetch"][href="${domain}"]`);
      if (!link) {
        link = document.createElement('link');
        link.rel = 'dns-prefetch';
        link.href = domain;
        document.head.appendChild(link);
      }
    });

    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = pageTitles[location.pathname] || 'LCTX Tire Changer | UAE & Saudi Arabia';

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.content = pageDescriptions[location.pathname] || 'LCTX Air-Powered Tire Changer - Professional portable tyre changing solutions.';

    let ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage) {
      ogImage = document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      document.head.appendChild(ogImage);
    }
    ogImage.content = 'https://res.cloudinary.com/dilx8zm2p/image/upload/f_auto,q_30,w_1200/v1759783025/1_iatrwm.jpg';

    let structuredData = document.querySelector('script[type="application/ld+json"]');
    if (!structuredData) {
      structuredData = document.createElement('script');
      structuredData.type = 'application/ld+json';
      document.head.appendChild(structuredData);
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "LCTX Air-Powered Tire Changer",
      "image": "https://res.cloudinary.com/dilx8zm2p/image/upload/f_auto,q_30,w_1200/v1759783025/1_iatrwm.jpg",
      "description": "Professional portable pneumatic tire changer machine. No electricity required, handles 12-21 inch wheels, 2 ton torque power.",
      "brand": {
        "@type": "Brand",
        "name": "LCTX"
      },
      "offers": {
        "@type": "Offer",
        "url": window.location.origin + createPageUrl("Product"),
        "priceCurrency": "USD",
        "price": "399",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "LCTX"
        }
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "500"
      }
    };

    structuredData.textContent = JSON.stringify(schema);

  }, [location.pathname]);

  // GTM dataLayer initialization
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.dataLayer) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
    }
  }, []);

  // Load GTM with defer - delay further for better initial load
  useEffect(() => {
    if (document.querySelector('script[src*="googletagmanager.com/gtm.js?id=GTM-KBXM9JHS"]')) {
      return;
    }

    const loadGTM = () => {
      const gtmScript = document.createElement('script');
      gtmScript.async = true;
      gtmScript.defer = true;
      gtmScript.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-KBXM9JHS';
      gtmScript.onerror = () => console.warn('GTM failed to load');
      document.head.appendChild(gtmScript);
    };

    const timeoutId = setTimeout(loadGTM, 10000);

    const loadOnInteraction = () => {
      clearTimeout(timeoutId);
      loadGTM();
      document.removeEventListener('scroll', loadOnInteraction);
      document.removeEventListener('mousemove', loadOnInteraction);
      document.removeEventListener('touchstart', loadOnInteraction);
    };

    document.addEventListener('scroll', loadOnInteraction, { once: true, passive: true });
    document.addEventListener('mousemove', loadOnInteraction, { once: true, passive: true });
    document.addEventListener('touchstart', loadOnInteraction, { once: true, passive: true });

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('scroll', loadOnInteraction);
      document.removeEventListener('mousemove', loadOnInteraction);
      document.removeEventListener('touchstart', loadOnInteraction);
    };
  }, []);

  // Google Analytics (G-BPYGTW91RX) is loaded once by index.html's deferred
  // analytics loader — no second container here (it doubled the JS cost).

  // Track pageview on route change
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-BPYGTW91RX', {
        page_path: location.pathname + location.search,
      });
    }
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'pageview',
        page: location.pathname + location.search
      });
    }
  }, [location]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 overscroll-y-none" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)', minHeight: '100vh' }}>
      {/* Pull-to-Refresh Indicator */}
      {isPulling && (
        <div 
          className="fixed top-0 left-0 right-0 z-[60] flex items-center justify-center bg-white dark:bg-gray-900 transition-all"
          style={{ 
            height: `${pullDistance}px`,
            opacity: pullDistance / 60,
            paddingTop: 'env(safe-area-inset-top)'
          }}
        >
          <svg className={`w-6 h-6 text-red-600 dark:text-red-500 ${pullDistance > 60 ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </div>
      )}
      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-KBXM9JHS"
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
          title="Google Tag Manager"
        ></iframe>
      </noscript>

      {/* WhatsApp Button - Lazy loaded */}
      <a
        href="https://wa.me/8618218291826"
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="fixed bottom-20 md:bottom-4 right-4 z-50 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-all select-none"
        style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
        aria-label="Contact us on WhatsApp"
        onClick={() => {
          base44.analytics.track({
            eventName: "referrer_traffic_click",
            properties: { 
              platform: "whatsapp",
              location: "floating_button"
            }
          });
        }}
      >
        <MessageCircle className="w-6 h-6 text-white" />
      </a>

      {/* Navigation - OPTIMIZED */}
      <nav className="fixed top-0 w-full bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm z-50 border-b border-gray-200/50 dark:border-gray-700/50 shadow-sm" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8" style={{ paddingLeft: 'max(0.75rem, env(safe-area-inset-left))', paddingRight: 'max(0.75rem, env(safe-area-inset-right))' }}>
          <div className="flex justify-between items-center h-14">
            {/* Back Button for Mobile Sub-Pages */}
            {isSubPage && (
              <button
                onClick={() => window.history.back()}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors select-none"
                aria-label="Go back"
              >
                <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            
            <Link to={createPageUrl("Home")} className={`flex items-center gap-2 select-none ${isSubPage ? 'md:flex' : ''}`}>
              <img
                src="https://wsrv.nl/?url=qtrypzzcjebvfcihiynt.supabase.co%2Fstorage%2Fv1%2Fobject%2Fpublic%2Fbase44-prod%2Fpublic%2Fuser_68e21a14fcc9a9d6bd88f4b4%2Fc7f22cd4f_cropped-LCTX-Logo-1.jpg&w=112&q=80&output=webp"
                alt="LCTX Logo"
                className="h-8 w-8"
                width="32"
                height="32"
                loading="eager"
                fetchpriority="high"
              />
              <div className="flex flex-col">
                <span className="text-sm md:text-xl font-bold leading-tight dark:text-white">LCTX</span>
                <span className="text-[8px] text-gray-600 dark:text-gray-400 hidden sm:block">Easy Tyres change anywhere</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`flex items-center gap-2 font-medium text-sm select-none ${
                    location.pathname === link.path ? "text-red-600 dark:text-red-500" : "text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-500"
                  }`}
                >
                  <link.icon className="w-4 h-4 select-none" />
                  {link.name}
                </Link>
              ))}
              <a
                href="https://www.youtube.com/@Eztirechanger"
                target="_blank"
                rel="noopener noreferrer nofollow"
                onClick={() => {
                  base44.analytics.track({
                    eventName: "referrer_traffic_click",
                    properties: { platform: "youtube", location: "top_nav" }
                  });
                }}
                className="flex items-center gap-2 font-medium text-sm select-none text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-500"
              >
                <Youtube className="w-4 h-4 select-none" />
                YouTube
              </a>
              <Link
                to={createPageUrl("Checkout")}
                className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 text-sm select-none"
              >
                <ShoppingCart className="w-4 h-4 select-none" />
                {t("order_now")}
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 transition-colors relative z-[102] select-none"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5 text-white select-none" />
              ) : (
                <Menu className="w-5 h-5 text-white select-none" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <>
          <div 
            className="md:hidden fixed inset-0 bg-black/60 dark:bg-black/80 z-[100] top-14"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="md:hidden fixed right-0 top-14 bottom-0 w-64 bg-white dark:bg-gray-900 shadow-2xl z-[101] overflow-y-auto">
            <div className="p-4 space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg font-semibold transition-colors text-sm select-none ${
                    location.pathname === link.path
                      ? "bg-red-600 dark:bg-red-700 text-white"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  <link.icon className="w-5 h-5 select-none" />
                  {link.name}
                </Link>
              ))}
              <a
                href="https://www.youtube.com/@Eztirechanger"
                target="_blank"
                rel="noopener noreferrer nofollow"
                onClick={() => {
                  setMobileMenuOpen(false);
                  base44.analytics.track({
                    eventName: "referrer_traffic_click",
                    properties: { platform: "youtube", location: "mobile_nav" }
                  });
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-lg font-semibold transition-colors text-sm select-none text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <Youtube className="w-5 h-5 select-none" />
                YouTube
              </a>
              <Link
                to={createPageUrl("Checkout")}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white px-4 py-3 rounded-lg font-bold w-full mt-4 text-sm select-none"
              >
                <ShoppingCart className="w-5 h-5 select-none" />
                {t("order_now")}
              </Link>
            </div>
          </div>
        </>
      )}

      {/* Bottom Navigation Bar - Mobile Only */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 z-50" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))', paddingLeft: 'env(safe-area-inset-left)', paddingRight: 'env(safe-area-inset-right)' }}>
        <div className="flex justify-around items-center px-2 py-1">
          {[
            { name: t("nav_home"), path: createPageUrl("Home"), icon: Home },
            { name: t("nav_product"), path: createPageUrl("Product"), icon: Package },
            { name: t("nav_how_to_use"), path: createPageUrl("HowToUse"), icon: Info },
            { name: t("nav_contact"), path: createPageUrl("Contact"), icon: Phone }
          ].map((link) => {
            const currentTab = getCurrentTab();
            const linkTab = link.path === tabPaths.home ? 'home' : 
                           link.path === tabPaths.product ? 'product' :
                           link.path === tabPaths.howToUse ? 'howToUse' : 'contact';
            const isActive = currentTab === linkTab;
            
            return (
              <button
                key={link.name}
                onClick={(e) => {
                  e.preventDefault();
                  
                  if (isActive) {
                    // Scroll to top if already on this tab
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } else {
                    // Save current state before switching
                    const currentTabKey = getCurrentTab();
                    if (currentTabKey) {
                      saveScrollPosition(currentTabKey, window.scrollY);
                      saveTabState(currentTabKey, location);
                    }
                    
                    // Check if we have saved state for the target tab
                    const savedState = getTabState(linkTab);
                    if (savedState && savedState.pathname !== link.path) {
                      // Navigate to saved location within tab
                      navigate(savedState.pathname + savedState.search);
                    } else {
                      // Navigate to tab root
                      navigate(link.path);
                    }
                  }
                }}
                className={`flex flex-col items-center justify-center py-2 px-2 min-w-[70px] select-none ${
                  isActive 
                    ? "text-red-600 dark:text-red-500" 
                    : "text-gray-600 dark:text-gray-400"
                }`}
              >
                <link.icon className={`w-6 h-6 mb-0.5 select-none ${isActive ? "scale-110" : ""}`} />
                <span className="text-xs font-medium leading-tight">{link.name}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* NOTE: no contentVisibility here — it made the browser lay out <main>
          as ~0 height first, painting the footer at the top and then pushing it
          a full viewport down (CLS 0.796). */}
      <main className="pt-14 pb-16 md:pb-0">
        <GeoGate>{children}</GeoGate>
      </main>

      {/* Footer - OPTIMIZED WITH LAZY LOADED IMAGES */}
      <footer className="relative mt-12 bg-gradient-to-br from-red-600 via-red-700 to-gray-900 dark:from-red-900 dark:via-gray-900 dark:to-black text-white overflow-hidden mb-16 md:mb-0" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="absolute inset-0 opacity-20 dark:opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-2">
                <img
                  src="https://wsrv.nl/?url=qtrypzzcjebvfcihiynt.supabase.co%2Fstorage%2Fv1%2Fobject%2Fpublic%2Fbase44-prod%2Fpublic%2Fuser_68e21a14fcc9a9d6bd88f4b4%2Fc7f22cd4f_cropped-LCTX-Logo-1.jpg&w=112&q=80&output=webp"
                  alt="LCTX Logo"
                  className="h-8 w-8 bg-white rounded-full p-1"
                  width="32"
                  height="32"
                  loading="lazy"
                />
                <span className="text-base font-black">LCTX</span>
              </div>
              <p className="text-gray-100 text-xs">{t("footer_tagline")}</p>
            </div>

            <div>
              <h3 className="font-black text-xs mb-2">{t("footer_quick_links")}</h3>
              <div className="space-y-1 text-xs">
                {navLinks.map((link) => (
                  <Link key={link.name} to={link.path} className="block text-gray-100 hover:text-white select-none">
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-black text-xs mb-2">{t("footer_policies")}</h3>
              <div className="space-y-1 text-xs">
                <Link to={createPageUrl("ShippingPolicy")} className="block text-gray-100 hover:text-white select-none">
                  {t("policy_shipping")}
                </Link>
                <Link to={createPageUrl("WarrantyPolicy")} className="block text-gray-100 hover:text-white select-none">
                  {t("policy_warranty")}
                </Link>
                <Link to={createPageUrl("RefundPolicy")} className="block text-gray-100 hover:text-white select-none">
                  {t("policy_refund")}
                </Link>
                <Link to={createPageUrl("PrivacyPolicy")} className="block text-gray-100 hover:text-white select-none">
                  {t("policy_privacy")}
                </Link>
              </div>
            </div>

            <div>
              <h3 className="font-black text-xs mb-2">{t("footer_warehouse")}</h3>
              {regionLoading ? (
                <div className="text-gray-100 text-xs">…</div>
              ) : (
                <div className="space-y-1 text-gray-100 text-xs">
                  <div>📍 {warehouse.address}</div>
                  <div>📞 {warehouse.phone}</div>
                  <div>✉️ {warehouse.email}</div>
                  <div>🕒 {warehouse.hours}</div>
                </div>
              )}
            </div>
          </div>

          <div className="mb-6">
            <h3 className="font-black text-xs mb-2">{t("footer_follow_us")}</h3>
            <div className="grid grid-cols-5 gap-2">
              {[
                { href: "https://www.facebook.com/lctxtyrechanger", icon: Facebook, name: "Facebook", color: "hover:bg-blue-600" },
                { href: "https://www.youtube.com/@Eztirechanger", icon: Youtube, name: "YouTube", color: "hover:bg-red-600" },
                { href: "https://www.tiktok.com/@personal.tire.changer", name: "TikTok", color: "hover:bg-black" },
                { href: "https://x.com/LCTXtirechanger", name: "X", color: "hover:bg-black" },
                { href: "https://www.instagram.com/lctxtirechanger/", icon: Instagram, name: "Instagram", color: "hover:bg-gradient-to-br hover:from-purple-600 hover:via-pink-600 hover:to-orange-600" }
              ].map((social, idx) => (
                <a 
                  key={idx} 
                  href={social.href} 
                  target="_blank" 
                  rel="noopener noreferrer nofollow" 
                  className="flex flex-col items-center gap-1 group select-none"
                  onClick={() => {
                    base44.analytics.track({
                      eventName: "referrer_traffic_click",
                      properties: { 
                        platform: social.name.toLowerCase(),
                        location: "footer_social"
                      }
                    });
                  }}
                >
                  <div className={`w-9 h-9 bg-white/20 dark:bg-white/10 rounded-lg flex items-center justify-center ${social.color} transition-all group-hover:scale-110 select-none`}>
                    {social.icon ? (
                      <social.icon className="w-4 h-4 text-white" />
                    ) : social.name === "TikTok" ? (
                      <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                      </svg>
                    ) : social.name === "X" ? (
                      <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                      </svg>
                    )}
                  </div>
                  <span className="text-[8px] font-semibold text-gray-200">{social.name}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="border-t border-white/20 pt-4 text-center text-gray-100">
            <p className="mb-3 text-xs">&copy; {new Date().getFullYear()} LCTX. {t("footer_rights")}</p>

            <div className="mt-3">
              <p className="text-[10px] text-gray-300 mb-2">{t("footer_payments")}</p>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <div className="bg-white rounded px-2 py-1 shadow-lg">
                  <img src="https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_111x69.jpg" alt="PayPal" className="h-4" width="56" height="35" loading="lazy" />
                </div>
                <div className="bg-white rounded px-2 py-1 shadow-lg">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" alt="Stripe" className="h-4" width="40" height="16" loading="lazy" />
                </div>
                <div className="bg-white rounded px-2 py-1 shadow-lg flex items-center gap-1">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/0/04/Visa.svg" alt="Visa" className="h-3" width="40" height="13" loading="lazy" />
                  <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-3" width="30" height="18" loading="lazy" />
                  <img src="https://upload.wikimedia.org/wikipedia/commons/f/fa/American_Express_logo_%282018%29.svg" alt="American Express" className="h-3" width="30" height="15" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default function Layout({ children }) {
  return (
    <RegionProvider>
      <LanguageProvider>
        <TabNavigationProvider>
          <LayoutContent>{children}</LayoutContent>
        </TabNavigationProvider>
      </LanguageProvider>
    </RegionProvider>
  );
}

// Import TabNavigationProvider
import { TabNavigationProvider, useTabNavigation } from "./components/TabNavigationContext";